import React, { createContext, useContext, useState, ReactNode } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';

interface LoadingState {
  isLoading: boolean;
  message?: string;
  overlay?: boolean;
}

interface LoadingContextType {
  loadingStates: Map<string, LoadingState>;
  setLoading: (key: string, loading: boolean, message?: string, overlay?: boolean) => void;
  clearLoading: (key: string) => void;
  clearAllLoading: () => void;
  isAnyLoading: () => boolean;
  getLoadingMessage: () => string | undefined;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

interface LoadingProviderProps {
  children: ReactNode;
}

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [loadingStates, setLoadingStates] = useState<Map<string, LoadingState>>(new Map());

  const setLoading = (key: string, loading: boolean, message?: string, overlay: boolean = false) => {
    setLoadingStates(prev => {
      const newStates = new Map(prev);
      if (loading) {
        newStates.set(key, { isLoading: true, message, overlay });
      } else {
        newStates.delete(key);
      }
      return newStates;
    });
  };

  const clearLoading = (key: string) => {
    setLoadingStates(prev => {
      const newStates = new Map(prev);
      newStates.delete(key);
      return newStates;
    });
  };

  const clearAllLoading = () => {
    setLoadingStates(new Map());
  };

  const isAnyLoading = () => {
    return loadingStates.size > 0;
  };

  const getLoadingMessage = () => {
    // Overlay olan loading'leri öncelikle al
    const overlayLoading = Array.from(loadingStates.values()).find(state => state.overlay);
    if (overlayLoading?.message) return overlayLoading.message;
    
    // Yoksa herhangi bir loading mesajını al
    const anyLoading = Array.from(loadingStates.values()).find(state => state.message);
    return anyLoading?.message;
  };

  const hasOverlayLoading = () => {
    return Array.from(loadingStates.values()).some(state => state.overlay);
  };

  const contextValue: LoadingContextType = {
    loadingStates,
    setLoading,
    clearLoading,
    clearAllLoading,
    isAnyLoading,
    getLoadingMessage
  };

  return (
    <LoadingContext.Provider value={contextValue}>
      {children}
      {/* Global overlay loading spinner */}
      {hasOverlayLoading() && (
        <LoadingSpinner
          size="xl"
          variant="primary"
          text={getLoadingMessage()}
          overlay={true}
        />
      )}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};
